import { useCallback, useEffect, useRef, useState } from "react";
import { Check, ChevronDown, ChevronUp, Download, Eye, EyeOff, FileText, Link2, Mail, PenLine, Pencil, Plus, Sparkles, Trash2, Upload } from "lucide-react";
import { platformClient, formatPlatformError } from "./platformApi";

const CARD = "border border-white/10 bg-coal p-6";
const LABEL = "text-[10px] uppercase tracking-[0.25em] text-ash";
const inputCls =
    "mt-2 w-full border-b border-white/20 bg-transparent pb-2.5 text-sm text-bone placeholder:text-ash/50 transition-colors focus:border-gold focus:outline-none";

export const AiPolishButton = ({ text, context, onApply, testid }) => {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const polish = async () => {
        if (!text || text.trim().length < 2) return;
        setBusy(true);
        setErr("");
        try {
            const { data } = await platformClient.post("/ai/polish", { text, context });
            onApply(data.polished);
        } catch (e) {
            setErr(formatPlatformError(e.response?.data?.detail));
            setTimeout(() => setErr(""), 4000);
        } finally {
            setBusy(false);
        }
    };
    return (
        <span className="inline-flex items-center gap-2">
            <button
                type="button"
                data-testid={testid}
                onClick={polish}
                disabled={busy}
                className="flex items-center gap-1.5 border border-gold/40 px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black disabled:opacity-50"
            >
                <Sparkles className="h-3 w-3" /> {busy ? "AI schrijft…" : "Verbeter met AI"}
            </button>
            {err ? <span className="text-[10px] text-red-400">{err}</span> : null}
        </span>
    );
};

const EMPTY_ITEM = { description: "", kind: "eenmalig", qty: 1, rate: "" };
const EMPTY = {
    doc_type: "offerte",
    client_id: "", client_name: "", client_email: "", client_address: "", client_contact: "", client_contact_function: "",
    client_kvk: "", client_btw: "", client_type: "consultancy", duration: "",
    title: "", intro: "", items: [{ ...EMPTY_ITEM }],
    valid_until: "", payment_term_days: 14, notes: "",
};

const DOC_TYPE_LABELS = { consultancy: "Consultancy", bemiddeling: "Bemiddeling", software: "Software / website" };

const BLOCK_DEFS = [
    { key: "title", label: "Titel van het document" },
    { key: "intro", label: "Inleidende tekst" },
    { key: "items", label: "Werkzaamheden & bedragen" },
    { key: "notes", label: "Voorwaarden & opmerkingen" },
    { key: "av_ref", label: "Verwijzing algemene voorwaarden" },
    { key: "akkoord", label: "Akkoord-pagina met handtekening" },
];

const normalizeBlocks = (saved) => {
    const byKey = Object.fromEntries((saved || []).map((b) => [b.key, b]));
    const ordered = (saved || []).filter((b) => BLOCK_DEFS.some((d) => d.key === b.key));
    const missing = BLOCK_DEFS.filter((d) => !byKey[d.key]).map((d) => ({ key: d.key, hidden: false, space_before: 6 }));
    return [...ordered, ...missing].map((b) => ({ hidden: false, space_before: 6, ...b }));
};

const DOC_TYPES = {
    offerte: { label: "Offerte", prefix: "VST", submit: "Offerte maken", edit: "Offerte bijwerken", list: "Eerdere offertes", empty: "Nog geen offertes gemaakt." },
    contract: { label: "Contract", prefix: "CTR", submit: "Contract maken", edit: "Contract bijwerken", list: "Contracten", empty: "Nog geen contracten gemaakt." },
    opdrachtbevestiging: { label: "Opdrachtbevestiging", prefix: "OPD", submit: "Opdrachtbevestiging maken", edit: "Opdrachtbevestiging bijwerken", list: "Opdrachtbevestigingen", empty: "Nog geen opdrachtbevestigingen gemaakt." },
};

export const AdminProposalsTab = () => {
    const [proposals, setProposals] = useState([]);
    const [clients, setClients] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [editing, setEditing] = useState(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorDoc, setEditorDoc] = useState(null);
    const [templates, setTemplates] = useState({});
    const [sendDoc, setSendDoc] = useState(null);
    const [adminEmail, setAdminEmail] = useState("");

    const load = useCallback(async () => {
        try {
            const [p, c, t] = await Promise.all([
                platformClient.get("/admin/proposals"),
                platformClient.get("/admin/clients"),
                platformClient.get("/admin/contract-templates"),
            ]);
            setProposals(p.data);
            setClients(c.data);
            setTemplates(t.data);
        } catch {}
    }, []);

    useEffect(() => {
        platformClient.get("/auth/me").then(({ data }) => setAdminEmail(data?.email || data?.user?.email || "")).catch(() => {});
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const insertTemplate = (type) => {
        const tpl = templates[type];
        if (!tpl) return;
        const filled = tpl.replaceAll("{payment_term_days}", String(form.payment_term_days || 14));
        setForm((f) => ({ ...f, notes: f.notes.trim() ? `${f.notes}\n\n${filled}` : filled }));
        flash(`Standaardtekst voor ${type} ingevoegd — pas gerust aan`);
    };

    const changeClientType = (type) => {
        setForm((f) => {
            const next = { ...f, client_type: type };
            // Alleen automatisch vullen als er nog geen eigen tekst staat
            if (!f.notes.trim() && templates[type]) {
                next.notes = templates[type].replaceAll("{payment_term_days}", String(f.payment_term_days || 14));
            }
            return next;
        });
    };

    const pickClient = (cid) => {
        if (!cid) return;
        const c = clients.find((x) => x.id === cid);
        if (!c) return;
        setForm((f) => ({
            ...f,
            client_id: c.id,
            client_name: c.company || f.client_name,
            client_email: c.email || f.client_email,
            client_address: c.address || f.client_address,
            client_contact: c.contact_name || "",
            client_contact_function: c.contact_function || "",
            client_kvk: c.kvk || "",
            client_btw: c.btw_number || "",
            client_type: c.client_type || "consultancy",
            rate_type: c.client_type === "bemiddeling" ? "provisie" : c.client_type === "software" ? "abonnement" : "uur",
        }));
        flash(`Klantgegevens van ${c.company} ingevuld`);
    };

    const flash = (m) => {
        setNotice(m);
        setTimeout(() => setNotice(""), 5000);
    };

    const setItem = (i, patch) => {
        setForm((f) => {
            const items = [...f.items];
            items[i] = { ...items[i], ...patch };
            return { ...f, items };
        });
    };
    const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));
    const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, x) => x !== i) }));

    const create = async (e) => {
        e.preventDefault();
        setError("");
        try {
            await platformClient.post("/admin/proposals", {
                ...form,
                line_items: form.items.filter((it) => it.description.trim()).map((it) => ({ ...it, qty: Number(it.qty) || 1, rate: Number(it.rate) || 0 })),
                payment_term_days: Number(form.payment_term_days) || 14,
            });
            setForm(EMPTY);
            flash("Voorstel aangemaakt — download de PDF hieronder");
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const download = async (p) => {
        const { data } = await platformClient.get(`/admin/proposals/${p.id}/pdf`, { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
        window.open(url, "_blank");
    };

    const downloadWord = async (p) => {
        const { data } = await platformClient.get(`/admin/proposals/${p.id}/docx`, { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `${p.number}.docx`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const uploadWord = async (p, file) => {
        if (!file) return;
        setError("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            await platformClient.post(`/admin/proposals/${p.id}/docx-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            flash(`Word-versie van ${p.number} opgeslagen als definitieve PDF`);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const revertCustom = async (p) => {
        await platformClient.delete(`/admin/proposals/${p.id}/custom-pdf`);
        flash(`Offerte ${p.number} terug naar platform-versie`);
        load();
    };

    const remove = async (id) => {
        await platformClient.delete(`/admin/proposals/${id}`);
        load();
    };

    const akkoord = async (id) => {
        try {
            const { data } = await platformClient.post(`/admin/proposals/${id}/akkoord`);
            flash(`Factuur ${data.number} aangemaakt — staat in Mijn facturen`);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    const openEditor = (p) => {
        setEditorDoc({
            id: p.id,
            number: p.number,
            issue_date: p.issue_date,
            doc_type: p.doc_type || "offerte",
            client_name: p.client_name || "",
            client_contact: p.client_contact || "",
            client_contact_function: p.client_contact_function || "",
            client_address: p.client_address || "",
            client_kvk: p.client_kvk || "",
            client_btw: p.client_btw || "",
            title: p.title || "",
            intro: p.intro || "",
            items: p.line_items?.length ? p.line_items.map((it) => ({ ...it, rate: String(it.rate) })) : [{ ...EMPTY_ITEM }],
            notes: p.notes || "",
            valid_until: p.valid_until || "",
            payment_term_days: p.payment_term_days || 14,
            blocks: normalizeBlocks(p.blocks),
            layout_overrides: p.layout_overrides || {},
        });
        setEditorOpen(true);
    };

    const saveEditor = async (edited, openPdf = false) => {
        const src = edited || editorDoc;
        if (!src) return;
        try {
            await platformClient.patch(`/admin/proposals/${src.id}`, {
                client_name: src.client_name,
                client_email: "",
                client_address: src.client_address,
                client_contact: src.client_contact,
                client_contact_function: src.client_contact_function,
                client_kvk: src.client_kvk,
                client_btw: src.client_btw,
                title: src.title,
                intro: src.intro,
                line_items: src.items.filter((it) => it.description.trim()).map((it) => ({ ...it, qty: Number(it.qty) || 1, rate: Number(it.rate) || 0 })),
                notes: src.notes,
                valid_until: src.valid_until,
                payment_term_days: Number(src.payment_term_days) || 14,
                blocks: src.blocks,
                layout_overrides: src.layout_overrides,
            });
            flash("Opgeslagen — je aanpassingen staan direct in de PDF");
            setEditorOpen(false);
            setEditorDoc(null);
            load();
            if (openPdf) download(src);
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        }
    };

    return (
        <div data-testid="admin-proposals-tab" className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-5">
            {editorOpen && editorDoc && (
                <ProposalEditor doc={editorDoc} onClose={() => { setEditorOpen(false); setEditorDoc(null); }} onSave={saveEditor} />
            )}
            {sendDoc && (
                <SendSignLinkModal proposal={sendDoc} adminEmail={adminEmail} onClose={() => { setSendDoc(null); load(); }} flash={flash} />
            )}
            <div className={`${CARD} lg:col-span-3`}>
                <p className={LABEL}><Plus className="mr-2 inline h-3.5 w-3.5 text-gold" />Nieuw document</p>
                <div className="col-span-2 mt-3 flex gap-2">
                    {Object.entries(DOC_TYPES).map(([key, cfg]) => (
                        <button
                            key={key}
                            type="button"
                            data-testid={`doctype-${key}`}
                            onClick={() => setForm((f) => ({ ...f, doc_type: key }))}
                            className={`border px-4 py-2 text-[11px] uppercase tracking-[0.15em] transition-colors ${
                                form.doc_type === key
                                    ? "border-gold bg-gold text-black"
                                    : "border-white/15 text-ash hover:border-gold/50 hover:text-gold"
                            }`}
                        >
                            {cfg.label}
                        </button>
                    ))}
                </div>
                <NumberingPanel docType={form.doc_type} onChanged={load} flash={flash} />
                <form onSubmit={create} className="mt-4 grid grid-cols-2 gap-5">
                    <label className="col-span-2 block">
                        <span className={LABEL}>Kies klant (vult gegevens automatisch in)</span>
                        <select data-testid="proposal-client-picker" defaultValue="" onChange={(e) => pickClient(e.target.value)} className={`${inputCls} bg-coal [&>option]:bg-coal [&>option]:text-bone`}>
                            <option value="" disabled>Kies een opgeslagen klant…</option>
                            {clients.map((c) => (
                                <option key={c.id} value={c.id}>{c.company}{c.contact_name ? ` — ${c.contact_name}` : ""}</option>
                            ))}
                        </select>
                    </label>
                    <label className="block">
                        <span className={LABEL}>Klantnaam</span>
                        <input data-testid="proposal-client" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required className={inputCls} />
                    </label>
                    <label className="block">
                        <span className={LABEL}>Klant e-mail</span>
                        <input data-testid="proposal-email" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className={inputCls} />
                    </label>
                    <label className="block">
                        <span className={LABEL}>Contactpersoon (handtekening)</span>
                        <input data-testid="proposal-contact" value={form.client_contact} onChange={(e) => setForm({ ...form, client_contact: e.target.value })} className={inputCls} />
                    </label>
                    <label className="block">
                        <span className={LABEL}>Functie contactpersoon</span>
                        <input data-testid="proposal-contact-function" value={form.client_contact_function} onChange={(e) => setForm({ ...form, client_contact_function: e.target.value })} placeholder="bijv. Directeur" className={inputCls} />
                    </label>
                    <label className="block">
                        <span className={LABEL}>Type opdracht</span>
                        <select data-testid="proposal-type" value={form.client_type} onChange={(e) => changeClientType(e.target.value)} className={`${inputCls} bg-coal [&>option]:bg-coal [&>option]:text-bone`}>
                            <option value="consultancy">Consultancy</option>
                            <option value="bemiddeling">Bemiddeling</option>
                            <option value="software">Software / website</option>
                        </select>
                    </label>
                    <label className="block">
                        <span className={LABEL}>Looptijd</span>
                        <input data-testid="proposal-duration" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="bijv. 3 maanden / doorlopend" className={inputCls} />
                    </label>
                    <label className="col-span-2 block">
                        <span className={LABEL}>Klantadres (regels met enter)</span>
                        <textarea data-testid="proposal-address" rows={2} value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} className={inputCls} />
                    </label>
                    <label className="col-span-2 block">
                        <span className={LABEL}>Titel van de offerte</span>
                        <input data-testid="proposal-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="bijv. Website voor Bakkerij Jansen" className={inputCls} />
                    </label>
                    <label className="col-span-2 block">
                        <span className={LABEL}>Inleiding</span>
                        <textarea data-testid="proposal-intro" rows={3} value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} placeholder={`Beste ${form.client_contact || form.client_name || "klant"}, hierbij mijn offerte zoals besproken.`} className={inputCls} />
                        <span className="mt-1 block">
                            <AiPolishButton testid="proposal-intro-ai" text={form.intro} context="offerte inleiding" onApply={(v) => setForm((f) => ({ ...f, intro: v }))} />
                        </span>
                    </label>
                    <div className="col-span-2 block">
                        <span className={LABEL}>Werkzaamheden</span>
                        <div className="mt-2 space-y-2">
                            {form.items.map((it, i) => (
                                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                                    <input data-testid={`item-desc-${i}`} value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} placeholder="bijv. Opzetten website" className={`${inputCls} col-span-5`} />
                                    <select data-testid={`item-kind-${i}`} value={it.kind} onChange={(e) => setItem(i, { kind: e.target.value })} className={`${inputCls} col-span-3 bg-coal [&>option]:bg-coal [&>option]:text-bone`}>
                                        <option value="eenmalig">Eenmalig</option>
                                        <option value="uur">Uren</option>
                                        <option value="maandelijks">Maandelijks</option>
                                    </select>
                                    <input data-testid={`item-qty-${i}`} type="number" min="0" step="0.5" value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} placeholder="aantal" className={`${inputCls} col-span-2`} />
                                    <input data-testid={`item-rate-${i}`} type="number" min="0" step="0.01" value={it.rate} onChange={(e) => setItem(i, { rate: e.target.value })} placeholder="€" className={`${inputCls} col-span-2`} />
                                    <button type="button" data-testid={`item-remove-${i}`} onClick={() => removeItem(i)} className="col-span-1 text-ash hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                                </div>
                            ))}
                        </div>
                        <button type="button" data-testid="item-add" onClick={addItem} className="mt-2 text-xs uppercase tracking-[0.15em] text-gold hover:text-bone">+ Werkzaamheid toevoegen</button>
                    </div>
                    {form.doc_type === "offerte" && (
                        <label className="block">
                            <span className={LABEL}>Geldig tot</span>
                            <input data-testid="proposal-valid" type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className={inputCls} />
                        </label>
                    )}
                    <label className="block">
                        <span className={LABEL}>Betalingstermijn (dagen)</span>
                        <input data-testid="proposal-payment-term" type="number" min="0" max="365" value={form.payment_term_days} onChange={(e) => setForm({ ...form, payment_term_days: e.target.value })} className={inputCls} />
                    </label>
                    <label className="col-span-2 block">
                        <span className={LABEL}>Voorwaarden & opmerkingen</span>
                        <textarea data-testid="proposal-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
                        <span className="mt-1 flex flex-wrap items-center gap-2">
                            <span className="text-[10px] text-ash/70">Standaardtekst invoegen:</span>
                            {Object.keys(DOC_TYPE_LABELS).map((t) => (
                                <button key={t} type="button" data-testid={`template-insert-${t}`} onClick={() => insertTemplate(t)} className="border border-white/15 px-2 py-0.5 text-[9px] uppercase tracking-wide text-ash hover:border-gold/50 hover:text-gold">
                                    {DOC_TYPE_LABELS[t]}
                                </button>
                            ))}
                        </span>
                    </label>
                    <div className="col-span-2">
                        {error ? <p data-testid="proposal-error" className="mb-2 text-sm text-red-400">{error}</p> : null}
                        {notice ? <p data-testid="proposal-notice" className="mb-2 text-xs uppercase tracking-[0.15em] text-gold">{notice}</p> : null}
                        <button data-testid="proposal-submit" type="submit" className="border border-gold/50 px-6 py-2.5 text-xs uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black">
                            {editing ? DOC_TYPES[form.doc_type].edit : DOC_TYPES[form.doc_type].submit}
                        </button>
                        {editing && (
                            <button type="button" data-testid="proposal-cancel-edit" onClick={() => { setEditing(null); setForm(EMPTY); }} className="ml-3 text-xs uppercase tracking-[0.15em] text-ash hover:text-bone">
                                Annuleren
                            </button>
                        )}
                    </div>
                </form>
            </div>

            <div className={`${CARD} lg:col-span-2`}>
                <p className={LABEL}><FileText className="mr-2 inline h-3.5 w-3.5 text-gold" />{DOC_TYPES[form.doc_type].list}</p>
                {proposals.filter((p) => (p.doc_type || "offerte") === form.doc_type).length === 0 ? (
                    <p data-testid="proposals-empty" className="mt-4 text-sm text-ash">{DOC_TYPES[form.doc_type].empty}</p>
                ) : (
                    <div className="mt-4 space-y-2">
                        {proposals.filter((p) => (p.doc_type || "offerte") === form.doc_type).map((p) => (
                            <div key={p.id} data-testid={`proposal-row-${p.id}`} className="flex items-center justify-between gap-3 border-b border-white/5 pb-2 text-xs">
                                <div className="min-w-0">
                                    <p className="truncate text-bone">{p.client_name} <span className="text-ash">— {p.title}</span></p>
                                    <p className="text-[10px] uppercase tracking-wide text-ash">{p.number} · {p.issue_date}{p.sign_link_sent_to ? ` · ✉ verstuurd naar ${p.sign_link_sent_to}` : ""}</p>
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                    {p.status !== "akkoord" ? (
                                        <>
                                            <button data-testid={`proposal-open-${p.id}`} onClick={() => openEditor(p)} className="flex items-center gap-1 border border-gold/50 px-2 py-1 text-[10px] uppercase text-gold hover:bg-gold/10">
                                                <Pencil className="h-3 w-3" /> Openen
                                            </button>
                                            <button data-testid={`proposal-akkoord-${p.id}`} onClick={() => akkoord(p.id)} className="flex items-center gap-1 border border-green-500/50 px-2 py-1 text-[10px] uppercase text-green-400 hover:bg-green-500/10">
                                                <Check className="h-3 w-3" /> {form.doc_type === "contract" ? "Ondertekend" : "Akkoord"}
                                            </button>
                                        </>
                                    ) : p.status === "ondertekend" ? (
                                        <span data-testid={`proposal-signed-badge-${p.id}`} className="text-[10px] uppercase tracking-wide text-green-400">Ondertekend</span>
                                    ) : p.status === "getekend" ? (
                                        <span data-testid={`proposal-signed-badge-${p.id}`} className="text-[10px] uppercase tracking-wide text-gold">1/2 getekend</span>
                                    ) : (
                                        <span data-testid={`proposal-akkoord-badge-${p.id}`} className="text-[10px] uppercase tracking-wide text-green-400">{form.doc_type === "contract" ? "Ondertekend" : "Akkoord"}</span>
                                    )}
                                    {p.sign_token ? (
                                        <>
                                            <button data-testid={`proposal-signlink-${p.id}`} onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/tekenen/${p.sign_token}`); flash(`Tekenlink voor ${p.number} gekopieerd — stuur hem naar je klant`); }} title="Kopieer de digitale-tekenen link voor je klant" className="flex items-center gap-1 border border-white/15 px-2 py-1 text-[10px] uppercase text-ash hover:border-gold/50 hover:text-gold">
                                                <Link2 className="h-3 w-3" /> Tekenlink
                                            </button>
                                            <a data-testid={`proposal-sign-self-${p.id}`} href={`/tekenen/${p.sign_token}?party=clarke`} target="_blank" rel="noreferrer" title="Zelf digitaal tekenen namens Clarke" className="flex items-center gap-1 border border-white/15 px-2 py-1 text-[10px] uppercase text-ash hover:border-gold/50 hover:text-gold">
                                                <PenLine className="h-3 w-3" /> Teken
                                            </a>
                                            <button data-testid={`proposal-send-${p.id}`} onClick={() => setSendDoc(p)} title="Tekenlink mailen vanaf je eigen adres (jij keurt eerst goed)" className="flex items-center gap-1 border border-gold/40 px-2 py-1 text-[10px] uppercase text-gold hover:bg-gold/10">
                                                <Mail className="h-3 w-3" /> {p.sign_link_sent_to ? "Opnieuw" : "Verstuur"}
                                            </button>
                                        </>
                                    ) : null}
                                    <button data-testid={`proposal-pdf-${p.id}`} onClick={() => download(p)} className="flex items-center gap-1 border border-gold/40 px-2 py-1 text-[10px] uppercase text-gold hover:bg-gold/10">
                                        <Download className="h-3 w-3" /> PDF
                                    </button>
                                    <button data-testid={`proposal-word-${p.id}`} onClick={() => downloadWord(p)} title="Download als Word om zelf te bewerken" className="flex items-center gap-1 border border-white/15 px-2 py-1 text-[10px] uppercase text-ash hover:border-gold/50 hover:text-gold">
                                        <FileText className="h-3 w-3" /> Word
                                    </button>
                                    <label data-testid={`proposal-upload-${p.id}`} title="Upload je bewerkte Word-versie (wordt de definitieve PDF)" className="flex cursor-pointer items-center gap-1 border border-white/15 px-2 py-1 text-[10px] uppercase text-ash hover:border-gold/50 hover:text-gold">
                                        <Upload className="h-3 w-3" />
                                        <input type="file" accept=".docx" className="hidden" onChange={(e) => uploadWord(p, e.target.files[0])} />
                                    </label>
                                    {p.custom_pdf_at ? (
                                        <button data-testid={`proposal-revert-${p.id}`} onClick={() => revertCustom(p)} title="Terug naar platform-versie" className="border border-gold/40 px-2 py-1 text-[9px] uppercase tracking-wide text-gold hover:bg-gold/10">
                                            Eigen versie ✕
                                        </button>
                                    ) : null}
                                    <button data-testid={`proposal-delete-${p.id}`} onClick={() => remove(p.id)} className="text-ash transition-colors hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// --- Mini-tekstverwerker: typen links, live voorbeeld rechts (zoals het op de PDF komt) ---
const mdToHtml = (md) => {
    const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    return esc(md || "")
        .replace(/\*\*([^*]+)\*\*/g, "<strong class='text-bone font-semibold'>$1</strong>")
        .replace(/\n/g, "<br/>");
};

const RichTextArea = ({ value, onChange, rows = 8, testid, placeholder, defaultPreview = true }) => {
    const ref = useRef(null);
    const [showPreview, setShowPreview] = useState(defaultPreview);

    const apply = (fn) => {
        const el = ref.current;
        if (!el) return;
        const s = el.selectionStart ?? value.length;
        const e = el.selectionEnd ?? value.length;
        const sel = value.slice(s, e);
        const { text, cursorStart, cursorEnd } = fn(sel, s, e);
        onChange(value.slice(0, s) + text + value.slice(e));
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(s + cursorStart, s + cursorEnd);
        });
    };

    const makeBold = () => apply((sel) => {
        // Toggle zoals in Word: is de selectie al dikgedrukt? Dan eruit halen. Bevat ze markers? Eerst schoonmaken.
        if (sel.startsWith("**") && sel.endsWith("**") && sel.length > 4) {
            const inner = sel.slice(2, -2).replaceAll("**", "");
            return { text: inner, cursorStart: 0, cursorEnd: inner.length };
        }
        const clean = (sel || "dikgedrukte tekst").replaceAll("**", "");
        return { text: `**${clean}**`, cursorStart: 2, cursorEnd: 2 + clean.length };
    });
    const makeHeading = () => apply((sel) => {
        const t = (sel || "Kopje").replaceAll("**", "").trim().toUpperCase() || "KOPJE";
        return { text: `\n\n**${t}**\n\n`, cursorStart: 4, cursorEnd: 4 + t.length };
    });
    const makeList = () => apply((sel) => {
        const t = (sel || "Punt").split("\n").map((ln) => `- ${ln}`).join("\n");
        return { text: t, cursorStart: 2, cursorEnd: t.length };
    });
    const newParagraph = () => apply(() => ({ text: "\n\n", cursorStart: 2, cursorEnd: 2 }));

    const toolBtn = "border border-white/15 px-2.5 py-1 text-[10px] uppercase tracking-wide text-ash transition-colors hover:border-gold/50 hover:text-gold";
    return (
        <div data-testid={`${testid}-richtext`}>
            <div className="flex flex-wrap items-center gap-1.5 border border-white/10 bg-black/30 px-2 py-1.5">
                <button type="button" data-testid={`${testid}-btn-bold`} onClick={makeBold} title="Dikgedrukt" className={`${toolBtn} font-bold`}>B</button>
                <button type="button" data-testid={`${testid}-btn-heading`} onClick={makeHeading} title="Kopje (dikgedrukt, eigen alinea)" className={toolBtn}>Kop</button>
                <button type="button" data-testid={`${testid}-btn-list`} onClick={makeList} title="Opsomming met streepjes" className={toolBtn}>- Lijst</button>
                <button type="button" data-testid={`${testid}-btn-paragraph`} onClick={newParagraph} title="Nieuwe alinea (witregel)" className={toolBtn}>¶ Alinea</button>
                <span className="mx-1 h-4 w-px bg-white/10" />
                <button type="button" data-testid={`${testid}-btn-preview`} onClick={() => setShowPreview(!showPreview)} className={`${toolBtn} ${showPreview ? "border-gold/50 text-gold" : ""}`}>
                    {showPreview ? "Verberg voorbeeld" : "Toon voorbeeld"}
                </button>
            </div>
            <div className={`grid gap-3 ${showPreview ? "grid-cols-2" : "grid-cols-1"}`}>
                <textarea
                    ref={ref}
                    data-testid={testid}
                    rows={rows}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className={`${inputCls} mt-0 border border-white/10 bg-black/20 p-3 text-sm leading-relaxed`}
                />
                {showPreview && (
                    <div
                        data-testid={`${testid}-preview`}
                        className="max-h-64 overflow-y-auto border border-gold/20 bg-white/[0.03] p-3 text-sm leading-relaxed text-ash"
                        dangerouslySetInnerHTML={{ __html: mdToHtml(value) || "<span class='text-ash/40'>Voorbeeld verschijnt hier…</span>" }}
                    />
                )}
            </div>
        </div>
    );
};

// --- Verzend-venster: tekenlink mailen vanaf je eigen adres, altijd eerst goedkeuren ---
const SendSignLinkModal = ({ proposal: p, adminEmail, onClose, flash }) => {
    const [to, setTo] = useState(p.client_email || "");
    const [subject, setSubject] = useState(`Offerte ${p.number} — ter ondertekening`);
    const [message, setMessage] = useState(
        `Beste ${p.client_contact || p.client_name},\n\nhierbij ontvang je offerte ${p.number} ter ondertekening. Via de knop hieronder bekijk je het document en plaats je direct je digitale handtekening.\n\nMet vriendelijke groet,\nRaymond Clarke`
    );
    const [busy, setBusy] = useState("");
    const [err, setErr] = useState("");

    const send = async (target) => {
        setBusy(target);
        setErr("");
        try {
            if (target === "test") {
                // Server stuurt ALLEEN naar je eigen admin-adres — kan nooit bij een klant terechtkomen
                await platformClient.post(`/admin/proposals/${p.id}/send-sign-link`, {
                    test: true, subject, message, origin: window.location.origin,
                });
                flash(`Test verzonden naar je eigen adres (${adminEmail})`);
            } else {
                if (!window.confirm(`Je staat op het punt de tekenlink voor ${p.number} te versturen naar:\n\n${to}\n\nVanaf je eigen adres, met jouw naam als afzender. Zeker weten?`)) {
                    setBusy("");
                    return;
                }
                await platformClient.post(`/admin/proposals/${p.id}/send-sign-link`, {
                    to, subject, message, origin: window.location.origin, confirm: true,
                });
                flash(`Tekenlink verzonden naar ${to}`);
            }
            onClose();
        } catch (e) {
            setErr(e.response?.data?.detail || "Versturen mislukt");
        } finally {
            setBusy("");
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="send-signlink-modal">
            <div className="w-full max-w-xl border border-white/10 bg-coal p-8">
                <p className={LABEL}>Tekenlink versturen — {p.number}</p>
                <p className="mt-1 text-[11px] text-ash">
                    Verzonden vanaf <span className="text-bone">jouw eigen e-mailadres</span>, met jouw naam. Er gaat niets automatisch de deur uit:
                    jij ziet de mail hieronder volledig en alleen jij drukt op versturen. Testen gaat altijd naar je eigen adres.
                </p>
                <label className="mt-5 block">
                    <span className={LABEL}>Aan (klant)</span>
                    <input data-testid="send-to" type="email" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} placeholder="klant@bedrijf.nl" />
                </label>
                <label className="mt-4 block">
                    <span className={LABEL}>Onderwerp</span>
                    <input data-testid="send-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} />
                </label>
                <label className="mt-4 block">
                    <span className={LABEL}>Bericht (de knop met tekenlink komt er automatisch onder)</span>
                    <textarea data-testid="send-message" rows={7} value={message} onChange={(e) => setMessage(e.target.value)} className={`${inputCls} leading-relaxed`} />
                </label>
                {err ? <p data-testid="send-error" className="mt-3 text-sm text-red-400">{err}</p> : null}
                <div className="mt-6 flex flex-wrap items-center gap-3">
                    <button data-testid="send-submit" onClick={() => send("klant")} disabled={!!busy || !to.trim()} className="bg-gold px-6 py-2.5 text-xs uppercase tracking-[0.15em] text-black transition-colors hover:bg-gold-light disabled:opacity-40">
                        {busy === "klant" ? "Versturen…" : "Verstuur naar klant"}
                    </button>
                    <button data-testid="send-test" onClick={() => send("test")} disabled={!!busy} className="border border-white/20 px-6 py-2.5 text-xs uppercase tracking-[0.15em] text-ash transition-colors hover:border-gold/50 hover:text-gold">
                        {busy === "test" ? "Versturen…" : `Test naar mijzelf${adminEmail ? ` (${adminEmail})` : ""}`}
                    </button>
                    <button data-testid="send-cancel" onClick={onClose} className="text-xs uppercase tracking-[0.15em] text-ash hover:text-bone">Annuleren</button>
                </div>
            </div>
        </div>
    );
};

// --- Nummering: volgende nummer instellen + bestaande hernummeren ---
const NumberingPanel = ({ docType, onChanged, flash }) => {
    const [info, setInfo] = useState(null);
    const [edit, setEdit] = useState(false);
    const [val, setVal] = useState(1);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await platformClient.get("/admin/numbering");
            setInfo(data[docType]);
            setVal(data[docType]?.next || 1);
        } catch {}
    }, [docType]);

    useEffect(() => {
        load();
    }, [load]);

    const save = async (renumber) => {
        if (renumber && !window.confirm(`Alle bestaande ${docType}-documenten van dit jaar worden op volgorde van aanmaak opnieuw genummerd vanaf ${info?.prefix}-${info?.year}-${String(val).padStart(3, "0")}. Eventueel geüploade Word-versies worden verwijderd zodat de PDF met het nieuwe nummer opnieuw wordt opgebouwd. Doorgaan?`)) return;
        setBusy(true);
        try {
            await platformClient.put("/admin/numbering", { doc_type: docType, next_number: Number(val), renumber_existing: renumber });
            await load();
            onChanged?.();
            setEdit(false);
            flash(renumber ? "Documenten opnieuw genummerd" : `Volgende nummer wordt ${info?.prefix}-${info?.year}-${String(val).padStart(3, "0")}`);
        } catch {} finally {
            setBusy(false);
        }
    };

    if (!info) return null;
    return (
        <div className="col-span-2 mt-2 flex flex-wrap items-center gap-3 border border-white/10 bg-black/20 px-4 py-2.5 text-[11px] text-ash" data-testid="numbering-panel">
            <span>Volgende nummer: <span className="text-gold">{info.prefix}-{info.year}-{String(info.next).padStart(3, "0")}</span></span>
            {!edit ? (
                <button type="button" data-testid="numbering-edit-open" onClick={() => setEdit(true)} className="border border-white/15 px-2 py-0.5 uppercase tracking-wide text-ash hover:border-gold/50 hover:text-gold">Aanpassen</button>
            ) : (
                <span className="flex items-center gap-2">
                    <span>Start bij:</span>
                    <input data-testid="numbering-input" type="number" min="1" max="9999" value={val} onChange={(e) => setVal(e.target.value)} className="w-16 border-b border-white/20 bg-transparent text-center text-bone focus:border-gold focus:outline-none" />
                    <button type="button" data-testid="numbering-save" onClick={() => save(false)} disabled={busy} className="border border-gold/50 px-2 py-0.5 uppercase tracking-wide text-gold hover:bg-gold/10">Zet teller</button>
                    <button type="button" data-testid="numbering-renumber" onClick={() => save(true)} disabled={busy} className="border border-white/15 px-2 py-0.5 uppercase tracking-wide text-ash hover:border-gold/50 hover:text-gold">Hernummer bestaande</button>
                    <button type="button" onClick={() => setEdit(false)} className="text-ash hover:text-bone">✕</button>
                </span>
            )}
        </div>
    );
};

// --- Offerte-editor (modal) — open, bewerk, sla op — met live PDF-voorbeeld ernaast ---
const ProposalEditor = ({ doc, onClose, onSave }) => {
    const [d, setD] = useState(doc);
    const [previewPages, setPreviewPages] = useState([]);
    const [pdfBusy, setPdfBusy] = useState(false);

    // Live PDF-voorbeeld: elke wijziging genereert na korte pauze nieuwe pagina-afbeeldingen
    useEffect(() => {
        let cancelled = false;
        const t = setTimeout(async () => {
            setPdfBusy(true);
            try {
                const { data } = await platformClient.post("/admin/proposals/preview", {
                    ...d,
                    line_items: d.items.filter((it) => it.description.trim()).map((it) => ({ ...it, qty: Number(it.qty) || 1, rate: Number(it.rate) || 0 })),
                    payment_term_days: Number(d.payment_term_days) || 14,
                });
                if (!cancelled) setPreviewPages(data.pages || []);
            } catch {} finally {
                if (!cancelled) setPdfBusy(false);
            }
        }, 800);
        return () => { cancelled = true; clearTimeout(t); };
    }, [d]);

    const set = (k, v) => setD((prev) => ({ ...prev, [k]: v }));
    const setItem = (i, patch) => setD((prev) => {
        const items = [...prev.items];
        items[i] = { ...items[i], ...patch };
        return { ...prev, items };
    });
    const addItem = () => setD((prev) => ({ ...prev, items: [...prev.items, { ...EMPTY_ITEM }] }));
    const removeItem = (i) => setD((prev) => ({ ...prev, items: prev.items.filter((_, x) => x !== i) }));

    return (
        <div className="fixed inset-0 z-50 bg-black/90 p-3" data-testid="proposal-editor">
            <div className="flex h-full flex-col gap-3 lg:flex-row">
                <div className="w-full overflow-y-auto border border-white/10 bg-coal p-6 lg:w-1/2">
                <div className="flex items-center justify-between border-b border-white/10 pb-4">
                    <div>
                        <p className={LABEL}>{DOC_TYPES[doc.doc_type || "offerte"].label} bewerken</p>
                        <p className="mt-1 font-display text-2xl text-bone">{d.number} — {d.title || "Zonder titel"}</p>
                    </div>
                    <button data-testid="editor-close" onClick={onClose} className="text-ash hover:text-bone">✕</button>
                </div>

                <div className="mt-6 space-y-6">
                    {/* Klantgegevens */}
                    <div>
                        <p className={LABEL}>Klantgegevens</p>
                        <div className="mt-3 grid grid-cols-2 gap-4">
                            <label className="block"><span className={LABEL}>Bedrijfsnaam</span><input data-testid="editor-client-name" value={d.client_name} onChange={(e) => set("client_name", e.target.value)} className={inputCls} /></label>
                            <label className="block"><span className={LABEL}>Contactpersoon</span><input data-testid="editor-contact" value={d.client_contact} onChange={(e) => set("client_contact", e.target.value)} className={inputCls} /></label>
                            <label className="block"><span className={LABEL}>Functie</span><input data-testid="editor-function" value={d.client_contact_function} onChange={(e) => set("client_contact_function", e.target.value)} className={inputCls} /></label>
                            <label className="block"><span className={LABEL}>KvK</span><input data-testid="editor-kvk" value={d.client_kvk} onChange={(e) => set("client_kvk", e.target.value)} className={inputCls} /></label>
                            <label className="col-span-2 block"><span className={LABEL}>Adres</span><textarea data-testid="editor-address" rows={2} value={d.client_address} onChange={(e) => set("client_address", e.target.value)} className={inputCls} /></label>
                        </div>
                    </div>

                    {/* Titel + intro */}
                    <div>
                        <p className={LABEL}>Inhoud</p>
                        <label className="mt-3 block"><span className={LABEL}>Titel</span><input data-testid="editor-title" value={d.title} onChange={(e) => set("title", e.target.value)} className={inputCls} /></label>
                        <div className="mt-3">
                            <span className={LABEL}>Inleiding</span>
                            <RichTextArea testid="editor-intro" rows={4} value={d.intro} onChange={(v) => set("intro", v)} placeholder={"Beste klant, hierbij…"} defaultPreview={false} />
                        </div>
                    </div>

                    {/* Werkzaamheden */}
                    <div>
                        <p className={LABEL}>Werkzaamheden</p>
                        <div className="mt-3 space-y-2">
                            {d.items.map((it, i) => (
                                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                                    <input data-testid={`editor-item-desc-${i}`} value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} placeholder="bijv. Opzetten website" className={`${inputCls} col-span-5`} />
                                    <select data-testid={`editor-item-kind-${i}`} value={it.kind} onChange={(e) => setItem(i, { kind: e.target.value })} className={`${inputCls} col-span-3 bg-coal [&>option]:bg-coal [&>option]:text-bone`}>
                                        <option value="eenmalig">Eenmalig</option>
                                        <option value="uur">Uren</option>
                                        <option value="maandelijks">Maandelijks</option>
                                    </select>
                                    <input data-testid={`editor-item-qty-${i}`} type="number" min="0" step="0.5" value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} placeholder="aantal" className={`${inputCls} col-span-2`} />
                                    <input data-testid={`editor-item-rate-${i}`} type="number" min="0" step="0.01" value={it.rate} onChange={(e) => setItem(i, { rate: e.target.value })} placeholder="€" className={`${inputCls} col-span-2`} />
                                    <button type="button" data-testid={`editor-item-remove-${i}`} onClick={() => removeItem(i)} className="col-span-1 text-ash hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                                </div>
                            ))}
                        </div>
                        <button type="button" data-testid="editor-item-add" onClick={addItem} className="mt-2 text-xs uppercase tracking-[0.15em] text-gold hover:text-bone">+ Werkzaamheid toevoegen</button>
                    </div>

                    {/* Voorwaarden — mini-tekstverwerker met live voorbeeld */}
                    <div>
                        <p className={LABEL}>Voorwaarden & opmerkingen — bewerk als een Word-document</p>
                        <p className="mt-1 text-[11px] text-ash">Selecteer tekst en klik op een knop. Rechts zie je direct hoe het op de PDF komt.</p>
                        <div className="mt-2">
                            <RichTextArea testid="editor-notes" rows={10} value={d.notes} onChange={(v) => set("notes", v)} placeholder={"Schrijf hier je voorwaarden…"} defaultPreview={false} />
                        </div>
                    </div>

                    {/* Opmaak & volgorde — blokken verschuiven, witruimte, lettergrootte, logo */}
                    <div>
                        <p className={LABEL}>Opmaak & volgorde — bepaal zelf hoe je PDF eruitziet</p>
                        <p className="mt-1 text-[11px] text-ash">Verschuif blokken met de pijltjes, verberg wat je niet nodig hebt, en stel de ruimte boven elk blok in.</p>
                        <div className="mt-3 space-y-1.5" data-testid="block-order-list">
                            {d.blocks.map((b, i) => {
                                const def = BLOCK_DEFS.find((x) => x.key === b.key);
                                const setBlock = (patch) => set("blocks", d.blocks.map((x, xi) => (xi === i ? { ...x, ...patch } : x)));
                                const move = (dir) => {
                                    const j = i + dir;
                                    if (j < 0 || j >= d.blocks.length) return;
                                    const next = [...d.blocks];
                                    [next[i], next[j]] = [next[j], next[i]];
                                    set("blocks", next);
                                };
                                return (
                                    <div key={b.key} data-testid={`block-row-${b.key}`} className={`flex items-center gap-2 border px-3 py-2 ${b.hidden ? "border-white/5 opacity-40" : "border-white/10"}`}>
                                        <span className="flex-1 text-xs text-bone">{def?.label || b.key}</span>
                                        {b.key === "akkoord" && (
                                            <label className="flex items-center gap-1.5 text-[10px] text-ash" title="Aan = altijd op een eigen pagina. Uit = op de vorige pagina als daar ruimte is">
                                                <input type="checkbox" data-testid="block-ownpage-akkoord" checked={!!b.own_page} onChange={(e) => setBlock({ own_page: e.target.checked })} className="h-3 w-3 accent-gold" />
                                                eigen pagina
                                            </label>
                                        )}
                                        <label className="flex items-center gap-1 text-[10px] text-ash">
                                            ruimte
                                            <input
                                                type="number" min="0" max="40" step="1"
                                                data-testid={`block-space-${b.key}`}
                                                value={b.space_before}
                                                onChange={(e) => setBlock({ space_before: Number(e.target.value) })}
                                                className="w-12 border-b border-white/20 bg-transparent text-center text-xs text-bone focus:border-gold focus:outline-none"
                                            />
                                            mm
                                        </label>
                                        <button type="button" data-testid={`block-up-${b.key}`} onClick={() => move(-1)} disabled={i === 0} className="p-1 text-ash hover:text-gold disabled:opacity-20" title="Omhoog"><ChevronUp className="h-4 w-4" /></button>
                                        <button type="button" data-testid={`block-down-${b.key}`} onClick={() => move(1)} disabled={i === d.blocks.length - 1} className="p-1 text-ash hover:text-gold disabled:opacity-20" title="Omlaag"><ChevronDown className="h-4 w-4" /></button>
                                        <button type="button" data-testid={`block-toggle-${b.key}`} onClick={() => setBlock({ hidden: !b.hidden })} className="p-1 text-ash hover:text-gold" title={b.hidden ? "Tonen" : "Verbergen"}>
                                            {b.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-4 grid grid-cols-3 gap-4">
                            <label className="block">
                                <span className={LABEL}>Logo-breedte: {d.layout_overrides.logo_width_mm || 38}mm</span>
                                <input data-testid="ov-logo-width" type="range" min="20" max="80" value={d.layout_overrides.logo_width_mm || 38} onChange={(e) => set("layout_overrides", { ...d.layout_overrides, logo_width_mm: Number(e.target.value) })} className="mt-2 w-full accent-gold" />
                            </label>
                            <label className="block">
                                <span className={LABEL}>Regelafstand: {Math.round((d.layout_overrides.line_height || 1) * 100)}%</span>
                                <input data-testid="ov-line-height" type="range" min="0.75" max="1.5" step="0.05" value={d.layout_overrides.line_height || 1} onChange={(e) => set("layout_overrides", { ...d.layout_overrides, line_height: Number(e.target.value) })} className="mt-2 w-full accent-gold" />
                            </label>
                            <label className="block">
                                <span className={LABEL}>Titelgrootte: {d.layout_overrides.title_size || 15}pt</span>
                                <input data-testid="ov-title-size" type="range" min="10" max="24" value={d.layout_overrides.title_size || 15} onChange={(e) => set("layout_overrides", { ...d.layout_overrides, title_size: Number(e.target.value) })} className="mt-2 w-full accent-gold" />
                            </label>
                        </div>
                    </div>

                    {/* Acties */}
                    <div className="flex items-center gap-3 border-t border-white/10 pt-6">
                        <button data-testid="editor-save" onClick={() => onSave(d)} className="border border-gold/50 px-8 py-3 text-xs uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black">
                            Opslaan
                        </button>
                        <button data-testid="editor-save-pdf" onClick={() => onSave(d, true)} className="bg-gold px-8 py-3 text-xs uppercase tracking-[0.15em] text-black transition-colors hover:bg-gold-light">
                            Opslaan & bekijk PDF
                        </button>
                        <button data-testid="editor-cancel" onClick={onClose} className="text-xs uppercase tracking-[0.15em] text-ash hover:text-bone">
                            Annuleren
                        </button>
                    </div>
                </div>
                </div>
                <div className="flex min-h-[50vh] w-full flex-col border border-white/10 bg-coal lg:w-1/2" data-testid="editor-preview-pane">
                    <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
                        <p className={LABEL}>Live voorbeeld — dit wordt je PDF ({previewPages.length} {previewPages.length === 1 ? "pagina" : "pagina's"})</p>
                        {pdfBusy ? <span data-testid="editor-preview-busy" className="text-[10px] uppercase tracking-wide text-gold">Bijwerken…</span> : null}
                    </div>
                    <div className="flex-1 space-y-3 overflow-y-auto bg-black/40 p-3">
                        {previewPages.length > 0 ? (
                            previewPages.map((pg, i) => (
                                <img key={i} data-testid={`editor-preview-page-${i}`} src={`data:image/png;base64,${pg}`} alt={`Pagina ${i + 1}`} className="w-full border border-white/10 shadow-2xl" />
                            ))
                        ) : (
                            <div data-testid="editor-preview-loading" className="flex min-h-[60vh] items-center justify-center text-xs text-ash">Voorbeeld wordt geladen…</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
